import React from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import styled from 'styled-components';
import countries from 'iso-3166-2';

import TextInput from '../input/TextInput';
import { CreateSubscriberSetupIntent } from '../../api/mutations';
import Popup from '../Popup';
import { PopupProps } from '../Popup/PopupProvider';
import Spacer from '../Spacer';
import Label from '../input/Label';
import { makeMutation } from '../../api/helpers';
import { getCurrentAccount } from '../../account_helpers';
import { useToastDispatch } from '../Toast/ToastProvider';
import Dropdown, { DropdownOptionType } from '../input/Dropdown';
import { Paragraph } from '../typography/Typography';
import Checkbox from '../input/Checkbox';
import DefaultError from '../../errors/defaultError';
import { CountryOption, COUNTRY_OPTIONS } from '../../helpers';

const StripeInput = styled.div`
  height: 44px;
  padding: 6px;
  margin: 0px 5px;
  border: 1px solid #e3e4e5;
  border-radius: 6px;
  padding: 12px;
  display: flex;
  align-items: center;
`;

const StripeElement = styled(CardElement)`
  width: 100%;
  height: 100%;
`;

const TaxCheckbox = styled.div`
  display: flex;
  align-items: center;
`;

function UpdateSubscriberCard({ onClose }: PopupProps) {
  const [name, setName] = React.useState('');
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);
  const [country, setCountry] = React.useState<CountryOption>(
    COUNTRY_OPTIONS[0]
  );
  const [state, setState] = React.useState<DropdownOptionType | null>(null);
  const [addr1, setAddr1] = React.useState<string>('');
  const [addr2, setAddr2] = React.useState<string>('');
  const [city, setCity] = React.useState<string>('');
  const [taxRegistered, setTaxRegistered] = React.useState<boolean>(false);
  const [taxID, setTaxID] = React.useState<string | null>(null);
  const dispatch = useToastDispatch();
  const stripe = useStripe();
  const elements = useElements();

  const onSave = async () => {
    setIsProcessing(true);

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return setIsProcessing(false);
    }

    const elm = elements.getElement(CardElement);

    if (!elm) {
      dispatch({
        alert: 'error',
        text: 'Unable to fetch card, please try again',
      });
      return setIsProcessing(false);
    }

    const token = await stripe.createToken(elm, {
      name: name,
      address_line1: addr1,
      address_line2: addr2,
      address_country: country.value,
      address_city: city,
      address_state: state?.value,
    });
    if (token.error) {
      dispatch({
        alert: 'error',
        text: 'Unable to tokenise card, please try again',
      });
      return setIsProcessing(false);
    }

    let resp;
    try {
      resp = await makeMutation(CreateSubscriberSetupIntent, {
        accountID: getCurrentAccount(),
        cardToken: token.token.id,
        taxID: taxRegistered ? { format: country.taxIDType, id: taxID } : null,
      });
    } catch (err) {
      if (err instanceof DefaultError) {
        dispatch({
          alert: 'error',
          text: err.helpText ?? err.message,
        });
      } else {
        dispatch({
          alert: 'error',
          text: 'Unable to save card, please try again',
        });
      }

      return setIsProcessing(false);
    }
    console.log('RES', resp);

    const result = await stripe.confirmCardSetup(
      resp.createSubscriberSetupIntent.clientSecret,
      {
        payment_method: {
          card: elm,
          billing_details: {
            name: name,
            address: {
              line1: addr1,
              line2: addr2,
              country: country.value,
              city: city,
              state: state?.value,
            },
          },
        },
      }
    );

    if (result.error) {
      dispatch({
        alert: 'warning',
        text: result.error?.message ?? 'Check your card details and try again',
      });
      return setIsProcessing(false);
    }

    dispatch({
      alert: 'success',
      text: 'Card update successfully',
    });
    console.log('res', result);

    setIsProcessing(false);
    if (onClose) onClose();
  };

  const stateOptions: DropdownOptionType[] = [];
  const k = countries.country(country?.value)?.sub;
  if (k) {
    for (const [key, value] of Object.entries(k)) {
      stateOptions.push({
        label: value.name,
        value: key.split('-')[1],
      });
    }
  }

  stateOptions.sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Popup
      onSave={onSave}
      onCancel={onClose}
      disabled={isProcessing}
      title={'Update Payment Method'}
    >
      <>
        <TextInput
          label='Full Name'
          fullWidth
          value={name}
          onChange={(name) => setName(name)}
          placeholder={'e.g John Doe'}
        />
        <Spacer vertical spacing={2} />
        <TextInput
          label='Address line 1'
          fullWidth
          value={addr1}
          onChange={(addr) => setAddr1(addr)}
          placeholder={'e.g 1 thornhill ave'}
        />
        <Spacer vertical spacing={2} />
        <TextInput
          label='Address line 2'
          fullWidth
          value={addr2}
          onChange={(addr) => setAddr2(addr)}
          placeholder={''}
        />
        <Spacer vertical spacing={2} />
        <TextInput
          label='City'
          fullWidth
          value={city}
          onChange={(city) => setCity(city)}
          placeholder={'e.g London'}
        />
        <Spacer vertical spacing={2} />
        {/* <TextInput
          label='Postal Code'
          fullWidth
          value={postcode}
          onChange={(postcode) => setPostcode(postcode)}
          placeholder={''}
        />
        <Spacer vertical spacing={2} /> */}
        <Dropdown
          label='Country'
          options={COUNTRY_OPTIONS}
          onChange={(option) => {
            const o = COUNTRY_OPTIONS.find((val) => val.value === option.value);
            if (o) setCountry(o);
          }}
          width={'100%'}
          defaultOption={COUNTRY_OPTIONS[0]}
        />
        <Spacer vertical spacing={2} />
        {k && (
          <>
            <Dropdown
              label='State, Province, County'
              options={stateOptions}
              onChange={(option) => {
                setState(option.value);
              }}
              width={'100%'}
              defaultOption={stateOptions[0]}
            />
            <Spacer vertical spacing={2} />
          </>
        )}
        <TaxCheckbox onClick={() => setTaxRegistered(!taxRegistered)}>
          <Checkbox selected={taxRegistered} />
          <Spacer horizontal spacing={1} />
          <Paragraph>I am a business registered for VAT/Sales Tax</Paragraph>
        </TaxCheckbox>
        <Spacer vertical spacing={2} />
        {taxRegistered && (
          <>
            <TextInput
              label={`${country.label} Tax Number`}
              fullWidth
              value={taxID ?? ''}
              onChange={(taxID) => setTaxID(taxID)}
              placeholder={''}
            />
            <Spacer vertical spacing={2} />
          </>
        )}
        <Label>Card</Label>
        <Spacer vertical spacing={1} />
        <StripeInput>
          <StripeElement
            options={{
              iconStyle: 'solid',
              style: {
                base: {
                  iconColor: '#5565e5',
                  color: '#0b0833',
                  fontWeight: 500,
                  fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
                  fontSize: '16px',
                  fontSmoothing: 'antialiased',
                  ':-webkit-autofill': {
                    color: '#fce883',
                  },
                  '::placeholder': {
                    color: '#cfcfd3',
                  },
                },
                invalid: {
                  iconColor: '#f85c5c',
                  color: '#f85c5c',
                },
              },
            }}
          />
        </StripeInput>
      </>
    </Popup>
  );
}

export default UpdateSubscriberCard;
